import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import RefreshIcon from "@material-ui/icons/Refresh";
import {
  Button,
  Divider,
  Form,
  Input,
  Popconfirm,
  Popover,
  Tooltip,
  Typography
} from "antd";
import _debounce from "lodash/debounce";
import { setSignificantFiguresSettings } from "store/features/app/appSlice";
import { setScreenshotPresets } from "store/features/userSettings/userSettingsSlice";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { UnitSystem } from "types/units";

import {
  addScreenshotPreset,
  deleteScreenshotPreset,
  editScreenshotPreset,
  getScreenshotPreset,
  getUserOrgScreenshots
} from "api/users";

import { ScreenshotPresetEditModel, ScreenshotPresetModel } from "models/model";

import { BaseSlideToggle } from "components/base";
import { RadioTab } from "components/base/RadioTabs";
import { BinResetButton } from "components/filter/BinSettings.styles";
import Icon_PrecisionDecrease from "components/icons/Icon_PrecisionDecrease";
import Icon_PrecisionIncrease from "components/icons/Icon_PrecisionIncrease";
import { type DefaultBinSettings as DefaultBinSettingsModel } from "components/user-settings/models";
import { useUserDefaults, useUserSettingMutation } from "components/user/hooks";

import MidstreamChartBackgroundColour from "./components/chart/MidstreamChartBackgroundColour";
import AutoZoomToggle from "./components/dashboard/AutoZoomToggle";
import DashboardSettings from "./components/dashboard/DashboardSettings";
import ScreenshotAbbreviations from "./components/screenshot/ScreenshotAbbreviations";
import ScreenshotPresetTable from "./components/screenshot/ScreenshotPresetTable";
import {
  DEFAULT_BIN_SETTINGS_SETTING,
  DEFAULT_SIGNIFICANT_FIGURES_SETTING,
  UNIT_SETTING
} from "./constants/userSetting.constants";
import { SignificantFiguresSetting, UnitSetting } from "./models";
import { DEFAULT_SIGNIFICANT_FIGURES } from "./models/SignificantFiguresSettings";
import { Info, InfoIconWrapper, InfoTitle, StyledRadioTabs } from "./shared/SharedStyle";

const tooltipText = `The following fields use Imperial units:
  - Production volume rates
  - Production volume totals

The following fields use SI units:
  - Depths
  - Lengths
  - Areas
  - Spacing
  - Frac fluid volumes
  - Proppant tonnage and concentration
  - Pressures`;

const dashboardTooltipText = `The dashboard display settings control how dashboards look for you.  They do not impact other users using the same dashboard.`;

const midstreamTooltipText =
  "The Midstream Chart Indicator toggle sets the background colour of midstream charts to be grey to allow differentiation from well charts.";

const strictRoundingTooltipText =
  "Controls whether integers are always rounded to the specified significant figures.\nFor example, with 3 significant figures: 1234.3 → 1230 (strict on) vs. 1234 (strict off).";

const { Title } = Typography;

const DisplaySettings = () => {
  // dispatches
  const dispatch = useDispatch();

  // Redux state
  const significantFiguresSettings = useSelector(
    (state: RootState) => state.app.significantFiguresSettings
  );

  const [localSigFigs, setLocalSigFigs] = useState(
    significantFiguresSettings.defaultSignificantFigures
  );

  const [localUseStrictRounding, setLocalUseStrictRounding] = useState(
    significantFiguresSettings.useStrictRounding
  );

  useEffect(() => {
    setLocalSigFigs(significantFiguresSettings.defaultSignificantFigures);
  }, [significantFiguresSettings.defaultSignificantFigures]);

  useEffect(() => {
    setLocalUseStrictRounding(significantFiguresSettings.useStrictRounding);
  }, [significantFiguresSettings.useStrictRounding]);

  // Debounced dispatch for significant figures changes
  const debouncedDispatchSignificantFigures = useRef(
    _debounce((settings) => {
      dispatch(setSignificantFiguresSettings(settings));
    }, 600)
  ).current;

  const defaults = useUserDefaults();

  // Cache the setting id locally to handle mutations before defaults refetch
  const cachedSettingIdRef = useRef<string | undefined>(
    defaults?.significantFiguresSetting?.id
  );

  // Sync cached id with defaults when it updates
  useEffect(() => {
    if (defaults?.significantFiguresSetting?.id) {
      cachedSettingIdRef.current = defaults?.significantFiguresSetting?.id;
    }
  }, [defaults?.significantFiguresSetting?.id]);
  const mutation = useUserSettingMutation<UnitSetting>(UNIT_SETTING);
  const defaultBinSettingsMutation = useUserSettingMutation<DefaultBinSettingsModel>(
    DEFAULT_BIN_SETTINGS_SETTING
  );
  const significantFiguresMutation = useUserSettingMutation<SignificantFiguresSetting>(
    DEFAULT_SIGNIFICANT_FIGURES_SETTING
  );

  // Debounced mutation for significant figures to prevent rapid-fire requests
  const debouncedMutateSignificantFigures = useRef(
    _debounce((payload: { id?: string } & SignificantFiguresSetting) => {
      significantFiguresMutation.mutate(payload, {
        onSuccess: (response) => {
          // Cache the id from response for subsequent mutations
          if (response?.id) {
            cachedSettingIdRef.current = response.id;
          }
        }
      });
    }, 1000)
  ).current;

  // state
  const [currentDefaultUnit, setCurrentDefaultUnit] = useState<number | undefined>(
    undefined
  );
  const [pendingUnitChange, setPendingUnitChange] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const currentUnitValue = currentDefaultUnit ?? defaults?.unitSetting?.unit ?? undefined;

  const getUnitLabel = (unit: number): string => {
    switch (unit) {
      case 0:
        return "Hybrid";
      case 1:
        return "Metric";
      case 2:
        return "US Imperial";
      default:
        return "Unknown";
    }
  };

  const handleDefaultUnitChange = (newUnit: number) => {
    const currentUnit = currentUnitValue;

    // If the new unit is the same as current, no need to confirm
    if (newUnit === currentUnit) {
      return;
    }

    // Temporarily set the current unit to show the clicked state
    setCurrentDefaultUnit(newUnit);

    // Store the pending change and show confirmation
    setPendingUnitChange(newUnit);
    setShowConfirm(true);
  };

  const handleConfirmUnitChange = () => {
    if (pendingUnitChange !== null) {
      mutation.mutate(
        {
          id: defaults?.unitSetting?.id,
          unit: pendingUnitChange
        },
        {
          onSuccess: () => {
            // before we reload the page, update bin settings to dynamic if condition is met
            if (
              defaults?.binSettings?.type === "BinSize" &&
              pendingUnitChange !== UnitSystem.Hybrid
            ) {
              defaultBinSettingsMutation.mutate(
                {
                  id: defaults?.binSettings?.id,
                  type: defaults?.binSettings?.type,
                  quantile: defaults?.binSettings?.quantile,
                  useDynamicBins: true
                },
                {
                  onSettled: () => {
                    window.location.reload();
                  }
                }
              );
            } else {
              window.location.reload();
            }
          }
        }
      );
      setCurrentDefaultUnit(pendingUnitChange);
    }
    setShowConfirm(false);
    setPendingUnitChange(null);
  };

  const handleCancelUnitChange = () => {
    // Revert to the original value from defaults
    setCurrentDefaultUnit(defaults?.unitSetting?.unit);
    setShowConfirm(false);
    setPendingUnitChange(null);
  };

  const handleSignificantFiguresToggle = (checked: boolean) => {
    // Immediate visual feedback
    setLocalUseStrictRounding(checked);

    const newSettings = {
      ...significantFiguresSettings,
      useStrictRounding: checked
    };
    debouncedDispatchSignificantFigures(newSettings);
    debouncedMutateSignificantFigures({
      id: cachedSettingIdRef.current,
      ...newSettings
    });
  };

  const handleSignificantFiguresChange = (delta: number) => {
    const newValue = Math.max(1, Math.min(10, localSigFigs + delta));

    // Update local state immediately for instant UI feedback
    setLocalSigFigs(newValue);

    const newSettings = {
      ...significantFiguresSettings,
      defaultSignificantFigures: newValue
    };

    // Debounce the Redux dispatch to prevent rapid updates
    debouncedDispatchSignificantFigures(newSettings);
    // Persist to backend with debounced mutation
    debouncedMutateSignificantFigures({
      id: cachedSettingIdRef.current,
      ...newSettings
    });
  };

  const handleResetSignificantFigures = () => {
    const defaultSettings = {
      defaultSignificantFigures: DEFAULT_SIGNIFICANT_FIGURES,
      useStrictRounding: false
    };
    setLocalSigFigs(DEFAULT_SIGNIFICANT_FIGURES);
    debouncedDispatchSignificantFigures(defaultSettings);
    debouncedMutateSignificantFigures({
      id: cachedSettingIdRef.current,
      ...defaultSettings
    });
  };

  const [screenshotPresetForm] = Form.useForm();

  // state
  const [currentPreset, setCurrentPreset] = useState<ScreenshotPresetEditModel>({
    title: "",
    width: "",
    height: "",
    titleFontSize: "",
    axisFontSize: "",
    axisLabelFontSize: "",
    topsLabelsFontSize: "",
    dataLabelsFontSize: "",
    gridLabelsFontSize: "",
    legendTitleSize: "",
    legendFontSize: ""
  });

  // queries and mutations
  const {
    data: userScreenshotPresets,
    isError,
    refetch
  } = useQuery("userScreenshotPreset", () => {
    return getScreenshotPreset();
  });
  const {
    data: orgScreenshotPresets,
    isLoading: isOrgLoading,
    isError: isOrgError,
    refetch: orgRefetch
  } = useQuery("OrgScreenshotPreset", () => {
    return getUserOrgScreenshots();
  });
  const editMutation = useMutation(
    async (preset: ScreenshotPresetModel) => {
      return await editScreenshotPreset(preset);
    },
    {
      onSuccess: async () => {
        toast.success("Successfully updated screenshot preset.");
        await refetch();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: async (error: any) => {
        if (error.response?.data) {
          toast.error(`An error occurred. ${error.response.data}`);
        } else {
          toast.error("Unable to connect to network.");
        }
        await refetch();
      }
    }
  );
  const deleteMutation = useMutation(
    async (preset: ScreenshotPresetModel) => {
      await deleteScreenshotPreset(preset.id);
    },
    {
      onSuccess: async () => {
        toast.success("Successfully deleted preset.");
        await refetch();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.response?.data) {
          toast.error(`An error occurred. ${error.response.data}`);
        } else {
          toast.error("Unable to connect to network.");
        }
      }
    }
  );
  const addMutation = useMutation(
    async (preset: ScreenshotPresetModel) => {
      await addScreenshotPreset(preset);
    },
    {
      onSuccess: async () => {
        setCurrentPreset({
          title: "",
          width: "",
          height: "",
          axisFontSize: "",
          titleFontSize: "",
          axisLabelFontSize: "",
          topsLabelsFontSize: "",
          dataLabelsFontSize: "",
          gridLabelsFontSize: "",
          legendTitleSize: "",
          legendFontSize: ""
        });
        screenshotPresetForm.resetFields();
        await refetch();
        await orgRefetch();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.response?.data) {
          toast.error(`An error occurred. ${error.response.data}`);
        } else {
          toast.error("Unable to connect to network.");
        }
      }
    }
  );

  // handlers
  function isValidPreset(preset: ScreenshotPresetEditModel) {
    return Object.keys(preset).every((k) => {
      const allPropertiesHaveValues = preset[k].length || preset[k];
      return allPropertiesHaveValues;
    });
  }

  function handleFormChange(changedValue) {
    const key = Object.keys(changedValue)[0];
    const value = changedValue[key];
    const nonDigitCharsRegex = /\D+/g;
    let nextValue = value.trim();
    if (key !== "title") {
      nextValue = nextValue.replace(nonDigitCharsRegex, "");
    }
    setCurrentPreset({ ...currentPreset, [key]: nextValue });
  }

  async function handleAddPreset() {
    if (!isValidPreset(currentPreset)) return;

    await addMutation.mutateAsync({
      title: currentPreset.title,
      width: Number(currentPreset.width),
      height: Number(currentPreset.height),
      axisFontSize: Number(currentPreset.axisFontSize),
      titleFontSize: Number(currentPreset.titleFontSize),
      axisLabelFontSize: Number(currentPreset.axisLabelFontSize),
      topsLabelsFontSize: Number(currentPreset.topsLabelsFontSize),
      dataLabelsFontSize: Number(currentPreset.dataLabelsFontSize),
      gridLabelsFontSize: Number(currentPreset.gridLabelsFontSize),
      legendTitleSize: Number(currentPreset.legendTitleSize),
      legendFontSize: Number(currentPreset.legendFontSize)
    });
  }

  async function handleEditPreset(preset: ScreenshotPresetModel) {
    editMutation.mutate(preset);
  }

  async function handleDeletePreset(preset: ScreenshotPresetModel) {
    if (!preset.id) {
      return;
    }
    deleteMutation.mutate(preset);
  }

  const getTableTitleMarkup = (title: string) => {
    return (
      <Title level={5} type="secondary">
        {title}
      </Title>
    );
  };

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedDispatchSignificantFigures.cancel();
      debouncedMutateSignificantFigures.cancel();
    };
  }, [debouncedDispatchSignificantFigures, debouncedMutateSignificantFigures]);

  useEffect(() => {
    if (!orgScreenshotPresets || !userScreenshotPresets) return;

    dispatch(setScreenshotPresets([...orgScreenshotPresets, ...userScreenshotPresets]));
  }, [userScreenshotPresets, orgScreenshotPresets, dispatch]);

  if (isError || isOrgError) {
    return <div>Unable to connect to backend service</div>;
  }

  return (
    <RootContainer>
      <Title level={3}>Display Settings</Title>

      <SectionContainer>
        <AutoZoomToggle />
      </SectionContainer>

      <InfoTitle level={5} type="secondary">
        Default units of measurement
        <Popover content={<Info>{tooltipText}</Info>} placement="right">
          <InfoIconWrapper />
        </Popover>
      </InfoTitle>

      <Popconfirm
        open={showConfirm}
        title={
          pendingUnitChange !== null ? (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Confirm Unit Change</div>
              <div>
                Units of measurement will affect how data is displayed across the
                application.
                <br />
                Are you sure you want to change the unit of measurement to{" "}
                <strong>{getUnitLabel(pendingUnitChange)}</strong>?<br />
                The application will refresh upon confirmation to ensure all data is
                displayed correctly.
              </div>
            </div>
          ) : (
            ""
          )
        }
        onConfirm={handleConfirmUnitChange}
        onCancel={handleCancelUnitChange}
        okText="Confirm"
        cancelText="Cancel"
        placement="bottomLeft">
        <StyledRadioTabs
          name="unitsMeasures"
          value={currentUnitValue}
          onChange={handleDefaultUnitChange}>
          <RadioTab value={0} label="Hybrid" />
          {/* Disable Metric support for the time being */}
          {/* <RadioTab value={1} label="Metric" /> */}
          <RadioTab value={2} label="US Imperial" />
        </StyledRadioTabs>
      </Popconfirm>

      <Divider />

      <InfoTitle level={4}>Significant Figures</InfoTitle>

      <SignificantFiguresWrapper>
        <SigFigSectionContainer>
          <InfoTitle level={5} type="secondary">
            Set Default Value
          </InfoTitle>
          <SigFigControlsContainer>
            <SigFigControls>
              <Tooltip title="Decrease Significant Figures" placement="bottom">
                <Button
                  type="default"
                  icon={<Icon_PrecisionDecrease />}
                  onClick={() => handleSignificantFiguresChange(-1)}
                />
              </Tooltip>

              <SigFigValue>{localSigFigs}</SigFigValue>

              <Tooltip title="Increase Significant Figures" placement="bottom">
                <Button
                  type="default"
                  icon={<Icon_PrecisionIncrease />}
                  onClick={() => handleSignificantFiguresChange(1)}
                />
              </Tooltip>
            </SigFigControls>

            <Tooltip title="Reset Default" placement="bottom">
              <BinResetButton
                type="link"
                icon={<RefreshIcon />}
                onClick={handleResetSignificantFigures}>
                Reset
              </BinResetButton>
            </Tooltip>
          </SigFigControlsContainer>
        </SigFigSectionContainer>

        <SigFigSectionContainer>
          <InfoTitle level={5} type="secondary">
            Strict Rounding
            <Popover content={<Info>{strictRoundingTooltipText}</Info>} placement="right">
              <InfoIconWrapper />
            </Popover>
          </InfoTitle>
          <SigFigControlsContainer>
            <BaseSlideToggle
              checked={localUseStrictRounding}
              classic
              onChange={handleSignificantFiguresToggle}
            />
          </SigFigControlsContainer>
        </SigFigSectionContainer>
      </SignificantFiguresWrapper>

      <Divider />

      <InfoTitle level={4}>
        Dashboard Display Settings
        <Popover content={<Info>{dashboardTooltipText}</Info>} placement="right">
          <InfoIconWrapper />
        </Popover>
      </InfoTitle>

      <DashboardSettings />
      <SectionContainer>
        <InfoTitle level={4}>
          Midstream Display Settings
          <Popover content={<Info>{midstreamTooltipText}</Info>} placement="right">
            <InfoIconWrapper />
          </Popover>
        </InfoTitle>
        <InfoTitle level={5} type="secondary">
          <MidstreamChartBackgroundColour />
        </InfoTitle>
      </SectionContainer>

      <Divider />

      <div className="screenshot-presets">
        <Title level={4}>Screenshots Settings</Title>

        <ScreenshotPresetTable
          dataSource={orgScreenshotPresets}
          loading={isOrgLoading}
          title={getTableTitleMarkup("Organization Screenshot Presets")}
        />

        <ScreenshotPresetTable
          dataSource={userScreenshotPresets}
          editable
          loading={isOrgLoading}
          title={getTableTitleMarkup("My Screenshot Presets")}
          onChange={handleEditPreset}
          onDelete={handleDeletePreset}
        />

        <StyledForm
          form={screenshotPresetForm}
          layout="vertical"
          size="large"
          onValuesChange={handleFormChange}>
          <Form.Item name="title" label="Title">
            <Input placeholder="Screenshot title" />
          </Form.Item>

          <Form.Item name="width" label="Width">
            <Input placeholder="px" />
          </Form.Item>

          <Form.Item name="height" label="Height">
            <Input placeholder="px" />
          </Form.Item>

          <Form.Item name="titleFontSize" label="Title Size">
            <Input placeholder="pt" />
          </Form.Item>

          <Form.Item name="axisFontSize" label="Axis Title">
            <Input placeholder="pt" />
          </Form.Item>

          <Form.Item name="axisLabelFontSize" label="Axis Labels">
            <Input placeholder="pt" />
          </Form.Item>

          <Form.Item name="topsLabelsFontSize" label="Tops Labels">
            <Input placeholder="pt" />
          </Form.Item>

          <Form.Item name="dataLabelsFontSize" label="Data Labels">
            <Input placeholder="pt" />
          </Form.Item>

          <Form.Item name="gridLabelsFontSize" label="Grid Labels">
            <Input placeholder="pt" />
          </Form.Item>

          <Form.Item name="legendTitleSize" label="Legend Title Size">
            <Input placeholder="pt" />
          </Form.Item>

          <Form.Item name="legendFontSize" label="Legend Label Size">
            <Input placeholder="pt" />
          </Form.Item>

          <Button
            type="text"
            loading={addMutation.isLoading}
            onClick={handleAddPreset}
            disabled={!isValidPreset(currentPreset)}>
            + Add
          </Button>
        </StyledForm>
        {Object.values(currentPreset).some((value) => value !== "") &&
          !isValidPreset(currentPreset) && (
            <Typography.Text type="warning">
              All fields must have a value before adding a new preset.
            </Typography.Text>
          )}
      </div>

      <SectionContainer>
        <ScreenshotAbbreviations />
      </SectionContainer>
    </RootContainer>
  );
};

export default DisplaySettings;

const RootContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--white);
  padding: 32px 100px;
  overflow: auto;

  .section-heading {
    font-size: 1.6rem;
    font-weight: var(--fontWeightMedium);
    padding: 0;
  }

  .section-heading {
    padding-bottom: 15px;
  }

  .preset-category {
    padding-left: 10px;
    margin-top: 5px;

    .category-heading {
      font-size: 1.4rem;
      font-weight: var(--fontWeightMedium);
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  input[type="number"] {
    -moz-appearance: textfield; /* Firefox */
  }
`;

const StyledForm = styled(Form)`
  display: grid;
  align-items: flex-end;
  grid-template-columns: 260px repeat(9, minmax(0, 1fr)) max-content;
  grid-column-gap: 12px;
  margin-top: 12px;

  .ant-form-item {
    margin-bottom: 0; // to adjust +Add button alignment with input fields
  }

  .ant-form-item-label {
    font-weight: 500;
  }

  .ant-form-item-label > label {
    height: unset; // removes default ant label height of 40px
  }

  .ant-form-item:not(:first-child) .ant-input {
    text-align: right; // align text to the right for all input fields except the first one
  }
`;

const SectionContainer = styled.div`
  padding-top: 18px;
`;

const SignificantFiguresWrapper = styled.div`
  display: flex;
  gap: 48px;
`;

const SigFigSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SigFigControlsContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  min-height: 32px;
`;

const SigFigControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 135px;

  .ant-btn {
    color: var(--color-icon);
    min-width: 32px;

    &:hover {
      background: var(--color-primary);
      color: white;
    }
  }
`;

const SigFigValue = styled.div`
  color: var(--color-primary);
  font-weight: bold;
  min-width: 20px;
  font-size: 16px;
  text-align: center;
`;
